import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useProjectStore } from '../store/project-store';
import { useAuthStore } from '../store/auth-store';
import { ensureDefaultStates } from '@shared/core/defaults';
const COLOR_PALETTE = [
    '#ef4444', '#f97316', '#f59e0b', '#22c55e', '#14b8a6',
    '#06b6d4', '#3b82f6', '#8b5cf6', '#a855f7', '#ec4899',
    '#94a3b8', '#64748b',
];
function SortableStateRow({ state, tasksCount, onEdit, onDelete, }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: state.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (_jsxs("div", { ref: setNodeRef, style: style, className: "flex items-center justify-between p-3 bg-bg-secondary rounded-lg border border-card-border", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("span", { ...attributes, ...listeners, className: "cursor-grab text-text-muted select-none", "aria-label": "Drag to reorder", children: "\u283F" }), _jsx("span", { className: "w-3 h-3 rounded-full shrink-0", style: { backgroundColor: state.color } }), _jsxs("div", { children: [_jsx("span", { className: "text-sm font-medium text-text-primary", children: state.name }), _jsxs("span", { className: "text-xs text-text-muted ml-2", children: ["(", tasksCount, " tasks)"] })] })] }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { onClick: () => onEdit(state.id), className: "text-xs text-text-muted hover:text-text-primary px-2 py-1 rounded hover:bg-bg-primary", "aria-label": `Edit ${state.name}`, children: "\u270E" }), _jsx("button", { onClick: () => onDelete(state.id), className: "text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded hover:bg-bg-primary", "aria-label": `Delete ${state.name}`, children: "\u2715" })] })] }));
}
export function ManageStatesModal({ projectId, onClose }) {
    const projects = useProjectStore((s) => s.projects);
    const tasks = useProjectStore((s) => s.tasks);
    const updateProjectStates = useProjectStore((s) => s.updateProjectStates);
    const project = projects.find((p) => p.project_id === projectId);
    const [states, setStates] = useState(() => ensureDefaultStates(project?.config.states).map(s => ({
        ...s,
        id: s.id?.trim() || `state-${s.order}`,
    })));
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState('');
    const [error, setError] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    const handleBackdropClick = useCallback((e) => {
        if (e.target === e.currentTarget)
            onClose();
    }, [onClose]);
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [onClose]);
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id)
            return;
        const oldIndex = states.findIndex((s) => s.id === active.id);
        const newIndex = states.findIndex((s) => s.id === over.id);
        if (oldIndex === -1 || newIndex === -1)
            return;
        const reordered = [...states];
        const [removed] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, removed);
        const withOrder = reordered.map((s, i) => ({ ...s, order: i }));
        setStates(withOrder);
    };
    const handleEdit = (id) => {
        const state = states.find((s) => s.id === id);
        if (!state)
            return;
        setEditingId(id);
        setEditName(state.name);
    };
    const handleSaveEdit = (id) => {
        if (!editName.trim())
            return;
        setStates(states.map((s) => (s.id === id ? { ...s, name: editName.trim() } : s)));
        setEditingId(null);
    };
    const handleDelete = (id) => {
        const state = states.find((s) => s.id === id);
        if (!state)
            return;
        const tasksCount = tasks.filter((t) => t.projectId === projectId && t.status === id).length;
        if (tasksCount > 0) {
            setError(`Move ${tasksCount} task(s) out of "${state.name}" first`);
            return;
        }
        const filtered = states.filter((s) => s.id !== id);
        const reordered = filtered.map((s, i) => ({ ...s, order: i }));
        setStates(reordered);
        setError(null);
    };
    const handleAdd = () => {
        if (!newName.trim())
            return;
        const baseId = newName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        const id = baseId || `state-${states.length}`;
        if (states.some((s) => s.id === id)) {
            setError(`A state with ID "${id}" already exists`);
            return;
        }
        const colorIndex = states.length % COLOR_PALETTE.length;
        const newState = {
            id,
            name: newName.trim(),
            color: COLOR_PALETTE[colorIndex],
            order: states.length,
        };
        setStates([...states, newState]);
        setNewName('');
        setAdding(false);
        setError(null);
    };
    const handleSave = async () => {
        updateProjectStates(projectId, states);
        const client = useAuthStore.getState().getClient();
        if (client) {
            try {
                await useProjectStore.getState().saveProjectContent(projectId, client);
            }
            catch {
                // non-blocking — local save already succeeded
            }
        }
        onClose();
    };
    return (_jsx("div", { className: "fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-16 px-4", onClick: handleBackdropClick, role: "dialog", "aria-label": "Manage workflow states", children: _jsxs("div", { className: "bg-card-bg border border-card-border rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto", children: [_jsxs("div", { className: "sticky top-0 bg-card-bg border-b border-card-border px-6 py-4 flex items-center justify-between", children: [_jsx("h2", { className: "text-lg font-bold text-text-primary", children: "Workflow States" }), _jsx("button", { onClick: onClose, className: "text-text-muted hover:text-text-primary text-xl", "aria-label": "Close", children: "\u00D7" })] }), _jsxs("div", { className: "px-6 py-4 space-y-3", children: [error && (_jsx("p", { className: "text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg", children: error })), _jsx("p", { className: "text-xs text-text-muted mb-2", children: "Drag to reorder. Tasks assigned to a state must be moved before deleting it." }), _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: _jsx(SortableContext, { items: states.map((s) => s.id), strategy: verticalListSortingStrategy, children: _jsx("div", { className: "space-y-2", children: states.map((state) => (_jsx("div", { children: editingId === state.id ? (_jsxs("div", { className: "flex items-center gap-2 p-3 bg-bg-secondary rounded-lg border border-card-border", children: [_jsx("span", { className: "w-3 h-3 rounded-full shrink-0", style: { backgroundColor: state.color } }), _jsx("input", { type: "text", value: editName, onChange: (e) => setEditName(e.target.value), onKeyDown: (e) => {
                                                        if (e.key === 'Enter')
                                                            handleSaveEdit(state.id);
                                                        if (e.key === 'Escape')
                                                            setEditingId(null);
                                                    }, className: "input flex-1 text-sm", autoFocus: true }), _jsx("button", { onClick: () => handleSaveEdit(state.id), className: "text-xs text-primary font-medium", children: "Save" }), _jsx("button", { onClick: () => setEditingId(null), className: "text-xs text-text-muted", children: "Cancel" })] })) : (_jsx(SortableStateRow, { state: state, tasksCount: tasks.filter((t) => t.projectId === projectId && t.status === state.id).length, onEdit: handleEdit, onDelete: handleDelete })) }, state.id))) }) }) }), adding ? (_jsxs("div", { className: "flex items-center gap-2 p-3 bg-bg-secondary rounded-lg border border-card-border", children: [_jsx("span", { className: "w-3 h-3 rounded-full shrink-0", style: { backgroundColor: COLOR_PALETTE[states.length % COLOR_PALETTE.length] } }), _jsx("input", { type: "text", value: newName, onChange: (e) => setNewName(e.target.value), onKeyDown: (e) => {
                                        if (e.key === 'Enter')
                                            handleAdd();
                                        if (e.key === 'Escape') {
                                            setAdding(false);
                                            setNewName('');
                                        }
                                    }, placeholder: "State name...", className: "input flex-1 text-sm", autoFocus: true }), _jsx("button", { onClick: handleAdd, className: "text-xs text-primary font-medium", children: "Add" }), _jsx("button", { onClick: () => { setAdding(false); setNewName(''); }, className: "text-xs text-text-muted", children: "Cancel" })] })) : (_jsx("button", { onClick: () => setAdding(true), className: "w-full py-2 text-sm text-primary font-medium border-2 border-dashed border-card-border rounded-lg hover:border-primary/50", children: "+ Add State" }))] }), _jsxs("div", { className: "sticky bottom-0 bg-card-bg border-t border-card-border px-6 py-4 flex justify-end gap-3", children: [_jsx("button", { onClick: onClose, className: "btn-secondary", children: "Cancel" }), _jsx("button", { onClick: handleSave, className: "btn-primary", children: "Save" })] })] }) }));
}
//# sourceMappingURL=ManageStatesModal.js.map